import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faUser, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../helpers/api';
import portalConfig from '../../services/portal-config.service';

const DirectAccess: React.FC = () => {
  const { userId, leadId } = useParams<{ userId: string; leadId: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [agentInfo, setAgentInfo] = useState<any>(null);

  useEffect(() => {
    validateDirectAccess();
  }, []);

  const validateDirectAccess = async () => {
    try {
      console.log(`🔗 Direct access: ${userId}/${leadId}`);
      
      // Call the direct access endpoint (remove /api prefix as it's already in axiosInstance)
      const response = await axiosInstance.get(`/portal/direct/${userId}/${leadId}`);
      
      if (response.data.success) {
        const { buyerContext, sanitizedBuyerContext } = response.data.data;
        
        // Store full buyer context for portal (includes contact info)
        localStorage.setItem('portalBuyerContext', JSON.stringify(buyerContext));
        
        // Store sanitized context for LLM usage (no PII)
        if (sanitizedBuyerContext) {
          localStorage.setItem('portalBuyerContextSanitized', JSON.stringify(sanitizedBuyerContext));
        }
        
        // Store agent info
        if (buyerContext.agentInfo) {
          localStorage.setItem('portalAgentInfo', JSON.stringify(buyerContext.agentInfo));
          setAgentInfo(buyerContext.agentInfo);
        }
        
        // Set direct access flag
        localStorage.setItem('portalDirectAccess', 'true');
        
        // Refresh portal config
        await portalConfig.refresh();
        
        // Show success state
        setIsLoading(false);
        
        // Brief success display before redirect
        setTimeout(() => {
          // Ensure buyer context is properly stored before redirect
          console.log('✅ [DirectAccess] Redirecting with buyer context:', buyerContext);
          
          // Clear any previous auto-search flag to ensure fresh trigger
          sessionStorage.removeItem('autoSearchTriggered');
          
          // Dispatch event before navigation to ensure it's caught
          window.dispatchEvent(new CustomEvent('portalBuyerContextLoaded', { 
            detail: buyerContext 
          }));
          
          // Store agent and lead IDs for reference on refresh
          const agentId = buyerContext.agentInfo?.id || userId || '';
          localStorage.setItem('portalAgentId', agentId);
          localStorage.setItem('portalLeadId', buyerContext.leadId || leadId || '');

          // Route based on agent's plan tier
          const agentSlug = buyerContext.agentInfo?.slug || agentId;
          const buyerLeadId = buyerContext.leadId || leadId || '';
          const agentPlan = buyerContext.agentInfo?.planId || buyerContext.agentInfo?.plan_id || '';

          // $69 Agent Hub → read-only buyer portal
          // $135+ AI Search / AI Pro → full AI chat (HomePageV3)
          if (agentPlan === 'agent-hub') {
            console.log('📋 [DirectAccess] Agent Hub plan → read-only buyer portal');
            navigate('/buyer-portal', {
              replace: true,
              state: { buyerContext, fromDirectAccess: true }
            });
          } else {
            // AI Search ($135) or AI Pro ($185) → full AI chat experience
            console.log('🤖 [DirectAccess] AI plan → full AI chat portal');
            navigate(`/${agentSlug}${buyerLeadId ? `?leadId=${buyerLeadId}` : ''}`, {
              replace: true,
              state: { buyerContext, fromDirectAccess: true }
            });
          }
        }, 2000);
      }
    } catch (err: any) {
      console.error('❌ Direct access validation failed:', err);
      setError(err.response?.data?.message || 'Unable to verify access. Please check your link.');
      setIsLoading(false);
    }
  };

  if (error) {
    return (
      <div className="min-vh-100 d-flex align-items-center bg-light">
        <Container>
          <Card className="shadow-lg border-0 mx-auto" style={{ maxWidth: '500px' }}>
            <Card.Body className="p-5 text-center">
              <Alert variant="danger" className="mb-0">
                <h5 className="mb-3">Access Error</h5>
                <p className="mb-0">{error}</p>
              </Alert>
            </Card.Body>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div className="min-vh-100 d-flex align-items-center bg-light">
      <Container>
        <Card className="shadow-lg border-0 mx-auto" style={{ maxWidth: '500px' }}>
          <Card.Body className="p-5 text-center">
            {isLoading ? (
              <>
                <div className="mb-4">
                  <FontAwesomeIcon 
                    icon={faHome} 
                    size="4x" 
                    className="text-primary mb-3" 
                  />
                </div>
                <h3 className="mb-3">Verifying Your Access</h3>
                <p className="text-muted mb-4">
                  Please wait while we validate your personalized link...
                </p>
                <Spinner animation="border" variant="primary" />
              </>
            ) : (
              <>
                <FontAwesomeIcon 
                  icon={faCheckCircle} 
                  size="4x" 
                  className="text-success mb-4" 
                />
                <h3 className="mb-3">Access Verified!</h3>
                <p className="text-muted mb-4">
                  Welcome! Redirecting you to your personalized portal...
                </p>
                
                {agentInfo && (
                  <div className="bg-light rounded p-3">
                    <div className="d-flex align-items-center justify-content-center">
                      <div className="rounded-circle bg-primary d-flex align-items-center justify-content-center me-3"
                           style={{ width: '50px', height: '50px' }}>
                        <FontAwesomeIcon icon={faUser} className="text-white" />
                      </div>
                      <div className="text-start">
                        <h6 className="mb-0">{agentInfo.name}</h6>
                        <small className="text-muted">Your Real Estate Agent</small>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default DirectAccess;